// Die Partie im Spielmodus: Stellung, gespielte Züge und wie oft jede Stellung schon vorkam.
// Beginnt in der Grundstellung oder in einer eigenen Stellung (berühmte Partie weiterspielen).
import { Chess, normalizeMove } from 'chessops/chess';
import { makeFen, parseFen } from 'chessops/fen';
import { makeSanAndPlay } from 'chessops/san';
import type { Move } from 'chessops/types';
import type { Key } from '@lichess-org/chessground/types';

/** last = so, wie das Brett den Zug zeigt (Rochade als Königszug) */
export type Played = { san: string; fen: string; last: [Key, Key]; move: Move };

export class PlayGame {
  pos: Chess;
  moves: Played[] = [];
  private seen = new Map<string, number>();

  constructor(readonly start?: string) {
    this.pos = PlayGame.initial(start);
    this.seen.set(this.key(), 1);
  }

  private static initial(start?: string): Chess {
    return start ? Chess.fromSetup(parseFen(start).unwrap()).unwrap() : Chess.default();
  }

  get fen() {
    return makeFen(this.pos.toSetup());
  }

  /** Stellung ohne Zugzähler, für die Stellungswiederholung */
  private key() {
    return this.fen.split(' ').slice(0, 4).join(' ');
  }

  /** wie oft die aktuelle Stellung schon auf dem Brett stand */
  get repetitions() {
    return this.seen.get(this.key()) ?? 0;
  }

  /** Zug ausführen, wenn er legal ist */
  play(move: Move, last: [Key, Key]): boolean {
    const m = normalizeMove(this.pos, move);
    if (!this.pos.isLegal(m)) return false;
    const san = makeSanAndPlay(this.pos, m);
    this.moves.push({ san, fen: this.fen, last, move: m });
    this.seen.set(this.key(), this.repetitions + 1);
    return true;
  }

  /** die letzten n Halbzüge zurücknehmen: von vorn nachspielen, damit Wiederholungen stimmen */
  undo(n: number) {
    const keep = this.moves.slice(0, Math.max(0, this.moves.length - n));
    this.pos = PlayGame.initial(this.start);
    this.moves = [];
    this.seen = new Map([[this.key(), 1]]);
    for (const m of keep) this.play(m.move, m.last);
  }
}
